import "swiper/css";
import "swiper/css/navigation";

import cn from "classnames";
import { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Typography } from "@mui/material";

//TODO: refactor modules from `view`
import { TextModule } from "@/src/component/partial/__module__";
import { TimeSlot } from "@/src/logic";

import styles from "./AppointmentPicker.module.scss";

type AppointmentPickerProps = {
  availableTimeSlots?: TimeSlot[];
  initialTimeSlotIndex?: number;
  initialTime?: Dayjs | null;
  daySlidesPerView?: number;
  timeSlidesPerView?: number;
  spaceBetween?: number;
  datePickerTitle?: string;
  datePickerSubtitle?: string;
  timePickerTitle?: string;
  daysMarginBottom?: boolean;
  onChange: (date: Dayjs | null, time?: Dayjs | null) => void;
};

function AppointmentPicker({
  availableTimeSlots,
  initialTimeSlotIndex = 0,
  initialTime,
  daySlidesPerView = 7,
  timeSlidesPerView = 7,
  spaceBetween = 16,
  datePickerTitle,
  datePickerSubtitle,
  timePickerTitle,
  daysMarginBottom,
  onChange,
}: AppointmentPickerProps) {
  const [selectedTimeSlotIndex, setSelectedTimeSlotIndex] = useState<number>(initialTimeSlotIndex);
  const [selectedTourTime, setSelectedTourTime] = useState<Dayjs | null | undefined>(initialTime);
  const [init, setInit] = useState(false);
  const availableTimes = availableTimeSlots?.[selectedTimeSlotIndex]?.times;

  useEffect(() => {
    onChange(availableTimeSlots?.[selectedTimeSlotIndex]?.date ?? null, selectedTourTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTourTime, selectedTimeSlotIndex, availableTimeSlots]);

  useEffect(() => {
    if (init) {
      return;
    }

    if (!availableTimes || (availableTimes ?? []).length === 0) {
      if ((availableTimeSlots ?? []).length > 0) {
        let iter = 0;
        for (const opt of availableTimeSlots ?? []) {
          if ((opt?.times ?? []).length > 0) {
            setSelectedTimeSlotIndex(iter);
            setSelectedTourTime(null);
            break;
          }
          iter++;
        }
      }
    }
    setInit(true);
  }, [availableTimes, init, availableTimeSlots, initialTimeSlotIndex]);

  function handleSelectDay(timeSlotIndex: number) {
    setSelectedTimeSlotIndex(timeSlotIndex);
    setSelectedTourTime(null);
  }

  function handleSelectTime(date: Dayjs) {
    setSelectedTourTime(date);
  }

  if ((availableTimeSlots ?? []).length === 0) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {datePickerTitle && (
        <TextModule
          text={{ text: datePickerTitle, tag: "h2" }}
          className={styles.title}
        />
      )}
      {datePickerSubtitle && (
        <TextModule
          text={{ text: datePickerSubtitle, tag: "body1" }}
          className={styles.subtitle}
        />
      )}
      <div className={`${cn(styles.scheduleTourDatePickerContainer, daysMarginBottom && styles.daysMarginBottom)} day-swiper`}>
        <div className={`${styles.scheduleTourSwiperNavigationPrev} swiper-button-prev`} />
        <Swiper
          spaceBetween={spaceBetween}
          modules={[Navigation]}
          slidesPerView={3}
          navigation={{ nextEl: ".day-swiper .swiper-button-next", prevEl: ".day-swiper .swiper-button-prev" }}
          breakpoints={{
            768: { slidesPerView: daySlidesPerView },
          }}
        >
          {(availableTimeSlots ?? []).map((timeSlot, i) => {
            if (timeSlot?.times?.length > 0) {
              return (
                <SwiperSlide key={i}>
                  <button
                    onClick={() => handleSelectDay(i)}
                    className={`${styles.slideContainer} ${styles.swiperDateHeight} ${i === selectedTimeSlotIndex ? styles.selected : ""}`}
                  >
                    <TextModule
                      text={{ text: timeSlot.date.format("ddd"), tag: "body1" }}
                      className={styles.weekDay}
                    />
                    <TextModule
                      text={{ text: timeSlot.date.format("D"), tag: "body1" }}
                      className={styles.day}
                    />
                    <TextModule
                      text={{ text: timeSlot.date.format("MMM"), tag: "body1" }}
                      className={styles.month}
                    />
                  </button>
                </SwiperSlide>
              );
            }
          })}
        </Swiper>
        <div className={`${styles.scheduleTourSwiperNavigationNext} swiper-button-next`} />
      </div>

      {timePickerTitle && (
        <TextModule
          text={{ text: timePickerTitle, tag: "h3" }}
          className={styles.timePickerTitle}
        />
      )}
      <div className={`${styles.scheduleTourDatePickerContainer} time-swiper`}>
        <div className={`${styles.scheduleTourSwiperNavigationPrev} swiper-button-prev`} />
        <Swiper
          spaceBetween={spaceBetween}
          modules={[Navigation]}
          slidesPerView={3}
          navigation={{ nextEl: ".time-swiper .swiper-button-next", prevEl: ".time-swiper .swiper-button-prev" }}
          breakpoints={{
            768: { slidesPerView: timeSlidesPerView },
          }}
        >
          {availableTimes && availableTimes.length > 0 ? (
            (availableTimes ?? []).map((date, i) => (
              <SwiperSlide key={i}>
                <button
                  onClick={() => handleSelectTime(date)}
                  className={`${cn(styles.slideContainer, styles.bottom)} ${date.isSame(selectedTourTime) ? styles.selected : ""}`}
                >
                  <TextModule
                    text={{ text: date.format("h:mm A"), tag: "body1" }}
                    className={styles.time}
                  />
                </button>
              </SwiperSlide>
            ))
          ) : (
            <></>
          )}
        </Swiper>
        {!availableTimes ||
          ((availableTimes ?? []).length === 0 && (
            <div>
              <Typography>No times available</Typography>
            </div>
          ))}
        {availableTimes && availableTimes.length > 0 ? <div className={`${styles.scheduleTourSwiperNavigationNext} swiper-button-next`} /> : <></>}
      </div>
    </div>
  );
}

export default AppointmentPicker;
